import React from 'react';
import { Component } from 'react';
import { CommsCampaignEmailAttachment, CommsCampaignMessage, CommsCampaignStage, HealthcareProvider } from '../interfaces';
import { Theme } from '../Theme';
import { TabsItemProps, TabsView } from './TabsView';
import { Button } from './Button';
import { TableRowEllipsisMenuButton } from './TableRowEllipsisMenuButton';
import { normalizeRichTextHtml, RichTextEditor, stripRichTextFormatting } from './RichTextEditor';

type PresentedMessage = CommsCampaignMessage & {
    id: string;
};

type MessageEditorSubmission = {
    message: CommsCampaignMessage;
    attachmentFile?: File | null;
    removeAttachment?: boolean;
};

type Props = {
    messages: Array<PresentedMessage>;
    healthcareProviderId: string;
    healthcareProviders: Array<HealthcareProvider>;
    isSuper: boolean;
    canCreateMessage: boolean;
    onCreateMessage: (submission: MessageEditorSubmission) => Promise<void>;
    onDeleteMessage: (messageId: string) => Promise<void>;
    onUpdateMessage: (messageId: string, submission: MessageEditorSubmission) => Promise<void>;
    onCopyMessagesFromHealthcareProvider: (healthcareProviderId: string) => Promise<number>;
};

type State = {
    selectedTabsItemIndex: number;
    showMessageDialog: boolean;
    showCopyDialog: boolean;
    isSubmitting: boolean;
    isDeleting: boolean;
    isCopying: boolean;
    copyFromHealthcareProviderId: string;
    selectedMessageId: string | null;
    hoveredMessageId: string | null;
    editingMessageId: string | null;
    messageForm: {
        amount: string;
        since: CommsCampaignMessage['since'];
        stages: Array<CommsCampaignStage>;
        sms: boolean;
        email: boolean;
        push: boolean;
        emailAttachment?: CommsCampaignEmailAttachment;
        attachmentFile: File | null;
        removeEmailAttachment: boolean;
        text: string;
    };
};

type NormalizedSince = Exclude<CommsCampaignMessage['since'], 'user_first_launched_app'>;

const SINCE_OPTIONS: Array<{ value: CommsCampaignMessage['since']; label: string }> = [
    { value: 'referral_account_created', label: 'the referral account was created' },
    { value: 'user_signed_up', label: 'the user signed-up' },
    { value: 'user_last_launched_app', label: 'the user last launched the app' },
    { value: 'user_completed_onboarding', label: 'the user completed the onboarding' },
    { value: 'last_message_sent', label: 'the last message was sent' },
    { value: 'user_last_active_on_app', label: 'the user was last active on the app' },
];

const STAGE_OPTIONS: Array<{ value: CommsCampaignStage; label: string }> = [
    { value: 'referral_created_not_signed_in', label: 'the referral account was created but the user has not signed in yet' },
    { value: 'signed_in_successfully', label: 'the user signed in successfully' },
    { value: 'completed_onboarding', label: 'the user completed the onboarding' },
    { value: 'first_used_app', label: 'the user first used the app' },
];

const SINCE_OPTIONS_BY_STAGE: Record<CommsCampaignStage, Array<NormalizedSince>> = {
    referral_created_not_signed_in: [
        'referral_account_created', 
        'last_message_sent'
    ],
    signed_in_successfully: [
        'referral_account_created', 
        'last_message_sent', 
        'user_signed_up', 
        'user_last_launched_app'
    ],
    completed_onboarding: [
        'referral_account_created',
        'last_message_sent',
        'user_signed_up',
        'user_last_launched_app',
        'user_completed_onboarding',
    ],
    first_used_app: [
        'referral_account_created',
        'last_message_sent',
        'user_signed_up',
        'user_last_launched_app',
        'user_completed_onboarding',
        'user_last_active_on_app',
    ],
};

export class MessagesView extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            selectedTabsItemIndex: 0,
            showMessageDialog: false,
            showCopyDialog: false,
            isSubmitting: false,
            isDeleting: false,
            isCopying: false,
            copyFromHealthcareProviderId: '',
            selectedMessageId: null,
            hoveredMessageId: null,
            editingMessageId: null,
            messageForm: this.getInitialMessageForm(),
        };
    }

    private tabs: Array<TabsItemProps> = [
        {
            label: 'Campaigns',
            id: 'campaigns',
            onSelect: () => {
                this.setState({ selectedTabsItemIndex: 0 });
            },
        },
    ];

    private getInitialMessageForm(): State['messageForm'] {
        const stages: Array<CommsCampaignStage> = ['referral_created_not_signed_in'];
        return {
            amount: '1',
            since: this.getDefaultSinceForStages(stages),
            stages,
            sms: true,
            email: false,
            push: false,
            emailAttachment: undefined,
            attachmentFile: null,
            removeEmailAttachment: false,
            text: '',
        };
    }

    private normalizeSince(since: string): NormalizedSince {
        if (since === 'user_first_launched_app') {
            return 'user_signed_up';
        }

        return since as NormalizedSince;
    }

    // Normalize message stages from old single-stage and current multi-stage data.
    private getMessageStages(message: Pick<CommsCampaignMessage, 'stages' | 'stage'>): Array<CommsCampaignStage> {
        if (Array.isArray(message.stages) && message.stages.length > 0) {
            return message.stages;
        }

        return message.stage ? [message.stage] : ['referral_created_not_signed_in'];
    }

    // Keep the "since" dropdown aligned with the union of selected stages.
    private getAvailableSinceValues(stages: Array<CommsCampaignStage>): Array<NormalizedSince> {
        const selectedStages: Array<CommsCampaignStage> = stages.length > 0 ? stages : ['referral_created_not_signed_in'];
        return SINCE_OPTIONS
            .map((option) => option.value as NormalizedSince)
            .filter((optionValue, optionIndex, optionValues) =>
                selectedStages.some((stage) => SINCE_OPTIONS_BY_STAGE[stage].includes(optionValue)) &&
                optionValues.indexOf(optionValue) === optionIndex
            );
    }

    // Default to the first valid "since" option whenever the stage set changes.
    private getDefaultSinceForStages(stages: Array<CommsCampaignStage>): NormalizedSince {
        return this.getAvailableSinceValues(stages)[0];
    }

    // Coerce legacy or no-longer-valid message combinations into allowed values.
    private getValidSinceForStages(
        stages: Array<CommsCampaignStage>,
        since: CommsCampaignMessage['since']
    ): NormalizedSince {
        const normalizedSince = this.normalizeSince(since);
        return this.getAvailableSinceValues(stages).includes(normalizedSince)
            ? normalizedSince
            : this.getDefaultSinceForStages(stages);
    }

    // Build the stage-dependent options list shown in the dialog.
    private getAvailableSinceOptions(stages: Array<CommsCampaignStage>) {
        const availableValues = this.getAvailableSinceValues(stages);
        return SINCE_OPTIONS.filter((option) => availableValues.includes(option.value as NormalizedSince));
    }

    private formatCondition(message: CommsCampaignMessage): string {
        const normalizedSince = this.normalizeSince(message.since);
        const sinceLabel = SINCE_OPTIONS.find(option => option.value === normalizedSince)?.label || normalizedSince;
        const stageLabels = this.getMessageStages(message)
            .map((stage) => STAGE_OPTIONS.find(option => option.value === stage)?.label || stage);
        return `${message.amount} ${message.unit} after ${sinceLabel} when ${stageLabels.join(' or ')}`;
    }

    // Render HTML-rich message text as plain text in list/table contexts.
    private stripMessageFormatting(text: string): string {
        return stripRichTextFormatting(text);
    }

    private formatChannels(message: CommsCampaignMessage): string {
        return message.channels
            .map((channel) => channel === 'push' ? 'Push notification' : channel.toUpperCase())
            .join(', ');
    }

    private getMessageForm(message: PresentedMessage): State['messageForm'] {
        const stages = this.getMessageStages(message);
        return {
            amount: String(message.amount),
            since: this.getValidSinceForStages(stages, message.since),
            stages,
            sms: message.channels.includes('sms'),
            email: message.channels.includes('email'),
            push: message.channels.includes('push'),
            emailAttachment: message.emailAttachment,
            attachmentFile: null,
            removeEmailAttachment: false,
            text: normalizeRichTextHtml(message.text),
        };
    }

    private showCreateDialog = () => {
        this.setState({
            showMessageDialog: true,
            editingMessageId: null,
            messageForm: this.getInitialMessageForm(),
        });
    }

    private showEditDialog = (message: PresentedMessage) => {
        this.setState({
            showMessageDialog: true,
            editingMessageId: message.id,
            messageForm: this.getMessageForm(message),
        });
    }

    private hideMessageDialog = () => {
        this.setState({
            showMessageDialog: false,
            isSubmitting: false,
            editingMessageId: null,
            messageForm: this.getInitialMessageForm(),
        });
    }

    // Only other healthcare providers can be selected as copy sources.
    private getCopySourceOptions(): Array<HealthcareProvider> {
        return this.props.healthcareProviders.filter((provider) => provider.id !== this.props.healthcareProviderId);
    }

    private showCopyDialog = () => {
        const sourceOptions = this.getCopySourceOptions();
        if (sourceOptions.length === 0) {
            window.alert('There are no other healthcare providers to copy from.');
            return;
        }

        this.setState({
            showCopyDialog: true,
            copyFromHealthcareProviderId: sourceOptions[0].id,
        });
    }

    private hideCopyDialog = () => {
        this.setState({
            showCopyDialog: false,
            isCopying: false,
            copyFromHealthcareProviderId: '',
        });
    }

    private handleCopyMessages = async () => {
        const sourceOptions = this.getCopySourceOptions();
        const sourceHealthcareProviderId = this.state.copyFromHealthcareProviderId || sourceOptions[0]?.id || '';
        const sourceProvider = sourceOptions.find((provider) => provider.id === sourceHealthcareProviderId);
        if (!sourceProvider) {
            window.alert('Select a healthcare provider to copy from.');
            return;
        }

        const confirmed = window.confirm(`Copy messages from ${sourceProvider.name} and replace the current messages?`);
        if (!confirmed) {
            return;
        }

        try {
            this.setState({ isCopying: true });
            const copiedMessageCount = await this.props.onCopyMessagesFromHealthcareProvider(sourceProvider.id);
            this.hideCopyDialog();
            window.alert(`Copied ${copiedMessageCount} ${copiedMessageCount === 1 ? 'message' : 'messages'} from ${sourceProvider.name}.`);
        } catch (error) {
            console.error('MESSAGES_VIEW: handleCopyMessages(): Error copying messages:', error);
            window.alert(error instanceof Error ? error.message : 'Failed to copy messages.');
            this.setState({ isCopying: false });
        }
    }

    private handleSubmit = async () => {
        const { amount, since, stages, sms, email, push, emailAttachment, attachmentFile, removeEmailAttachment, text } = this.state.messageForm;
        const { editingMessageId } = this.state;
        const parsedAmount = Number.parseInt(amount, 10);
        const plainText = this.stripMessageFormatting(text);
        const channels = [
            ...(sms ? ['sms'] : []),
            ...(email ? ['email'] : []),
            ...(push ? ['push'] : []),
        ] as Array<'sms' | 'email' | 'push'>;

        if (!Number.isInteger(parsedAmount) || parsedAmount < 0) {
            window.alert('Number must be a whole number greater than or equal to 0.');
            return;
        }

        if (channels.length === 0) {
            window.alert('Select at least one channel.');
            return;
        }

        if (stages.length === 0) {
            window.alert('Select at least one stage.');
            return;
        }

        if (!plainText) {
            window.alert('Text is required.');
            return;
        }

        try {
            this.setState({ isSubmitting: true });
            const nextEmailAttachment = email ? (removeEmailAttachment ? undefined : emailAttachment) : undefined;
            const message = {
                amount: parsedAmount,
                unit: 'days' as const,
                since,
                stages,
                channels,
                text: text.trim(),
                ...(nextEmailAttachment ? { emailAttachment: nextEmailAttachment } : {}),
            };
            const submission = {
                message,
                attachmentFile: email ? attachmentFile : null,
                removeAttachment: email ? removeEmailAttachment : !!emailAttachment,
            };

            if (editingMessageId) {
                await this.props.onUpdateMessage(editingMessageId, submission);
            } else {
                await this.props.onCreateMessage(submission);
            }

            this.hideMessageDialog();
        } catch (error) {
            console.error('MESSAGES_VIEW: handleSubmit(): Error saving message:', error);
            window.alert(error instanceof Error ? error.message : 'Failed to save message.');
            this.setState({ isSubmitting: false });
        }
    }

    private handleDeleteMessage = async (messageId: string) => {
        // Delete the selected campaign message from its table-row menu.
        if (!messageId) {
            return;
        }

        const confirmed = window.confirm('Are you sure you want to delete this message?');
        if (!confirmed) {
            return;
        }

        try {
            this.setState({ isDeleting: true });
            await this.props.onDeleteMessage(messageId);
            this.setState({
                isDeleting: false,
                selectedMessageId: this.state.selectedMessageId === messageId ? null : this.state.selectedMessageId,
                hoveredMessageId: null,
            });
        } catch (error) {
            console.error('MESSAGES_VIEW: handleDeleteMessage(): Error deleting message:', error);
            window.alert(error instanceof Error ? error.message : 'Failed to delete message.');
            this.setState({ isDeleting: false });
        }
    }

    private getStageOrder(stages: Array<CommsCampaignStage>): number {
        const stageOrders = stages
            .map((stage) => STAGE_OPTIONS.findIndex((option) => option.value === stage))
            .filter((index) => index !== -1);
        const index = stageOrders.length > 0 ? Math.min(...stageOrders) : -1;
        return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    }

    private getSinceOrder(since: CommsCampaignMessage['since']): number {
        const index = SINCE_OPTIONS.findIndex((option) => option.value === this.normalizeSince(since));
        return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    }

    private renderCampaigns() {
        const sortedMessages = [...this.props.messages].sort((a, b) => {
            const stageOrderDifference = this.getStageOrder(this.getMessageStages(a)) - this.getStageOrder(this.getMessageStages(b));
            if (stageOrderDifference !== 0) {
                return stageOrderDifference;
            }

            const sinceOrderDifference = this.getSinceOrder(a.since) - this.getSinceOrder(b.since);
            if (sinceOrderDifference !== 0) {
                return sinceOrderDifference;
            }

            const amountDifference = a.amount - b.amount;
            if (amountDifference !== 0) {
                return amountDifference;
            }

            return a.text.localeCompare(b.text);
        });

        return (
            <div style={styles.tableContainer}>
                <div style={styles.actionsContainer}>
                    {this.props.isSuper && (
                        <Button
                            onClick={this.showCopyDialog}
                            label='Copy from...'
                            type='secondary'
                            disabled={
                                !this.props.canCreateMessage ||
                                this.state.isDeleting ||
                                this.state.isSubmitting ||
                                this.state.isCopying ||
                                this.getCopySourceOptions().length === 0
                            }
                        />
                    )}
                    <Button
                        onClick={this.showCreateDialog}
                        label='New'
                        icon={require('./assets/icons/add_selected.svg').default}
                        type='primary'
                        disabled={!this.props.canCreateMessage || this.state.isDeleting || this.state.isCopying}
                    />
                </div>

                {sortedMessages.length === 0 ? (
                    <div style={styles.emptyState}>
                        {this.props.canCreateMessage
                            ? 'No messages are configured for this healthcare provider yet.'
                            : 'No campaign is configured for this healthcare provider.'}
                    </div>
                ) : (
                    <div style={styles.tableScrollArea}>
                        <table style={styles.table}>
                            <thead>
                                <tr style={styles.headerRow}>
                                    <th style={styles.conditionHeaderCell}>Condition</th>
                                    <th style={styles.channelHeaderCell}>Send</th>
                                    <th style={styles.textHeaderCell}>Text</th>
                                    <th style={styles.actionsHeaderCell}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedMessages.map((message) => {
                                    const isSelected = this.state.selectedMessageId === message.id;
                                    const isHovered = this.state.hoveredMessageId === message.id;

                                    return (
                                        <tr
                                            key={message.id}
                                            onClick={() => this.setState({
                                                selectedMessageId: isSelected ? null : message.id,
                                            })}
                                            style={{
                                                backgroundColor: isSelected ? Theme.Colours.tableHeader : (isHovered ? 'rgba(0, 0, 0, 0.05)' : 'transparent'),
                                                cursor: 'pointer',
                                            }}
                                            onMouseEnter={() => this.setState({ hoveredMessageId: message.id })}
                                            onMouseLeave={() => {
                                                if (this.state.hoveredMessageId === message.id) {
                                                    this.setState({ hoveredMessageId: null });
                                                }
                                            }}
                                        >
                                            <td style={styles.conditionCell}>{this.formatCondition(message)}</td>
                                            <td style={styles.channelCell}>{this.formatChannels(message)}</td>
                                            <td style={styles.textCell}>{this.stripMessageFormatting(message.text)}</td>
                                            <td style={styles.actionsCell}>
                                                <TableRowEllipsisMenuButton
                                                    ariaLabel='Message options'
                                                    isVisible={isHovered}
                                                    options={[{
                                                        label: 'Delete',
                                                        destructive: true,
                                                        disabled: this.state.isDeleting || this.state.isSubmitting || this.state.isCopying,
                                                        onClick: () => this.handleDeleteMessage(message.id),
                                                    }, {
                                                        label: 'Edit',
                                                        disabled: this.state.isDeleting || this.state.isSubmitting || this.state.isCopying,
                                                        onClick: () => this.showEditDialog(message),
                                                    }]}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }

    private renderMessageDialog() {
        if (!this.state.showMessageDialog) {
            return null;
        }

        const { messageForm, isSubmitting, editingMessageId } = this.state;
        const isEditing = editingMessageId != null;
        const availableSinceOptions = this.getAvailableSinceOptions(messageForm.stages);

        return (
            <div style={styles.dialogOverlay}>
                <div style={styles.dialog}>
                    <h3 style={styles.dialogTitle}>{isEditing ? 'Edit message' : 'Create message'}</h3>

                    <div style={styles.formField}>
                        <label style={styles.label}>Condition</label>
                        <p style={styles.helperText}>
                            Select the user’s current stage first. The "days since" dropdown then shows the triggering events available for that stage.
                        </p>
                        <div style={styles.sentenceBuilder}>
                            <div style={styles.sentenceRow}>
                                <span style={styles.inlineText}>When</span>
                                <div style={styles.stageOptionsBox}>
                                    <div style={styles.stageOptions}>
                                        {STAGE_OPTIONS.map((option) => (
                                            <label key={option.value} style={styles.stageOptionLabel}>
                                                <input
                                                    type='checkbox'
                                                    checked={messageForm.stages.includes(option.value)}
                                                    onChange={(e) => {
                                                        const nextStages = e.target.checked ?
                                                            [...messageForm.stages, option.value] :
                                                            messageForm.stages.filter((stage) => stage !== option.value);

                                                        this.setState({
                                                            messageForm: {
                                                                ...messageForm,
                                                                stages: nextStages,
                                                                since: this.getValidSinceForStages(nextStages, messageForm.since),
                                                            }
                                                        });
                                                    }}
                                                    disabled={isSubmitting}
                                                />
                                                {option.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div style={styles.sentenceRow}>
                                <input
                                    type='number'
                                    min='0'
                                    value={messageForm.amount}
                                    onChange={(e) => this.setState({
                                        messageForm: {
                                            ...messageForm,
                                            amount: e.target.value,
                                        }
                                    })}
                                    style={styles.numberInput}
                                    disabled={isSubmitting}
                                />
                                <span style={styles.inlineText}>days since</span>
                                <select
                                    value={messageForm.since}
                                    onChange={(e) => this.setState({
                                        messageForm: {
                                            ...messageForm,
                                            since: e.target.value as CommsCampaignMessage['since'],
                                        }
                                    })}
                                    style={styles.inlineWideSelect}
                                    disabled={isSubmitting}
                                >
                                    {availableSinceOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div style={styles.sentenceRow}>
                                <span style={styles.inlineText}>send</span>
                                <div style={styles.channelOptions}>
                                    <label style={styles.channelOptionLabel}>
                                        <input
                                            type='checkbox'
                                            checked={messageForm.sms}
                                            onChange={(e) => this.setState({
                                                messageForm: {
                                                    ...messageForm,
                                                    sms: e.target.checked,
                                                }
                                            })}
                                            disabled={isSubmitting}
                                        />
                                        an SMS
                                    </label>
                                    <label style={styles.channelOptionLabel}>
                                        <input
                                            type='checkbox'
                                            checked={messageForm.email}
                                            onChange={(e) => this.setState({
                                                messageForm: {
                                                    ...messageForm,
                                                    email: e.target.checked,
                                                }
                                            })}
                                            disabled={isSubmitting}
                                        />
                                        an email
                                    </label>
                                    <label style={styles.channelOptionLabel}>
                                        <input
                                            type='checkbox'
                                            checked={messageForm.push}
                                            onChange={(e) => this.setState({
                                                messageForm: {
                                                    ...messageForm,
                                                    push: e.target.checked,
                                                }
                                            })}
                                            disabled={isSubmitting}
                                        />
                                        a push notification
                                    </label>
                                </div>
                            </div>

                            <div style={styles.sentenceRow}>
                                <span style={styles.inlineText}>with the text:</span>
                            </div>
                        </div>
                    </div>

                    <div style={styles.formField}>
                        <RichTextEditor
                            value={messageForm.text}
                            unpadded={messageForm.sms}
                            onChange={(nextText: string) => this.setState({
                                messageForm: {
                                    ...messageForm,
                                    text: nextText,
                                }
                            })}
                            disabled={isSubmitting}
                            ariaLabel='Message text'
                            minHeight='160px'
                            maxHeight='360px'
                        />
                    </div>

                    {messageForm.email && (
                        <div style={styles.formField}>
                            <label style={styles.label}>Email attachment</label>
                            <div style={styles.attachmentField}>
                                <input
                                    type='file'
                                    onChange={(e) => this.setState({
                                        messageForm: {
                                            ...messageForm,
                                            attachmentFile: e.target.files?.[0] || null,
                                            removeEmailAttachment: false,
                                        }
                                    })}
                                    style={styles.fileInput}
                                    disabled={isSubmitting}
                                />
                                {messageForm.attachmentFile && (
                                    <div style={styles.attachmentHelperText}>
                                        Selected file: {messageForm.attachmentFile.name}
                                    </div>
                                )}
                                {!messageForm.attachmentFile && messageForm.emailAttachment && !messageForm.removeEmailAttachment && (
                                    <>
                                        <div style={styles.attachmentHelperText}>
                                            Current attachment: {messageForm.emailAttachment.originalName}
                                        </div>
                                        <button
                                            type='button'
                                            onClick={() => this.setState({
                                                messageForm: {
                                                    ...messageForm,
                                                    removeEmailAttachment: true,
                                                }
                                            })}
                                            style={styles.removeAttachmentButton}
                                            disabled={isSubmitting}
                                        >
                                            Remove attachment
                                        </button>
                                    </>
                                )}
                                {messageForm.removeEmailAttachment && (
                                    <div style={styles.attachmentHelperText}>
                                        Attachment will be removed when you save this message.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div style={styles.dialogButtons}>
                        <Button onClick={this.hideMessageDialog} label='Cancel' type='secondary' disabled={isSubmitting} />
                        <Button onClick={this.handleSubmit} label={isSubmitting ? (isEditing ? 'Saving...' : 'Creating...') : (isEditing ? 'Save' : 'Create')} type='primary' disabled={isSubmitting} />
                    </div>
                </div>
            </div>
        );
    }

    private renderCopyDialog() {
        if (!this.state.showCopyDialog) {
            return null;
        }

        const sourceOptions = this.getCopySourceOptions();
        const selectedProviderId = this.state.copyFromHealthcareProviderId || sourceOptions[0]?.id || '';

        return (
            <div style={styles.dialogOverlay}>
                <div style={styles.dialog}>
                    <h3 style={styles.dialogTitle}>Copy from...</h3>

                    <div style={styles.formField}>
                        <label style={styles.label}>Healthcare provider</label>
                        <select
                            value={selectedProviderId}
                            onChange={(e) => this.setState({ copyFromHealthcareProviderId: e.target.value })}
                            style={styles.select}
                            disabled={this.state.isCopying}
                        >
                            {sourceOptions.map((provider) => (
                                <option key={provider.id} value={provider.id}>
                                    {provider.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div style={styles.dialogButtons}>
                        <Button onClick={this.hideCopyDialog} label='Cancel' type='secondary' disabled={this.state.isCopying} />
                        <Button onClick={this.handleCopyMessages} label={this.state.isCopying ? 'Copying...' : 'Copy'} type='primary' disabled={this.state.isCopying || sourceOptions.length === 0} />
                    </div>
                </div>
            </div>
        );
    }

    render() {
        return (
            <div style={styles.container}>
                <TabsView options={this.tabs} selectedTabsItemIndex={this.state.selectedTabsItemIndex} />
                {this.renderCampaigns()}
                {this.renderMessageDialog()}
                {this.renderCopyDialog()}
            </div>
        );
    }
}

const styles = {
    container: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column' as const,
        flexGrow: 1,
        backgroundColor: Theme.Colours.background,
        minHeight: 0,
    },
    tableContainer: {
        width: 'calc(100% - 72px)',
        background: Theme.Colours.background,
        padding: '36px',
        display: 'flex',
        flexDirection: 'column' as const,
        flex: 1,
        minHeight: 0,
    },
    actionsContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '16px',
        gap: '12px',
        flexShrink: 0,
    },
    tableScrollArea: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto' as const,
    },
    table: {
        width: '100%',
        backgroundColor: Theme.Colours.workArea,
        borderRadius: '8px',
        overflow: 'hidden',
        border: Theme.Colours.border,
        tableLayout: 'fixed' as const,
    },
    headerRow: {
        backgroundColor: Theme.Colours.tableHeader,
        color: Theme.Colours.text,
    },
    conditionHeaderCell: {
        ...Theme.Fonts.body,
        fontWeight: 'bold' as const,
        padding: '16px 24px',
        textAlign: 'left' as const,
        borderBottom: Theme.Colours.border,
        width: '320px',
    },
    channelHeaderCell: {
        ...Theme.Fonts.body,
        fontWeight: 'bold' as const,
        padding: '16px 24px',
        textAlign: 'left' as const,
        borderBottom: Theme.Colours.border,
        width: '220px',
    },
    textHeaderCell: {
        ...Theme.Fonts.body,
        fontWeight: 'bold' as const,
        padding: '16px 24px',
        textAlign: 'left' as const,
        borderBottom: Theme.Colours.border,
    },
    actionsHeaderCell: {
        ...Theme.Fonts.body,
        fontWeight: 'bold' as const,
        padding: '16px 24px',
        textAlign: 'right' as const,
        borderBottom: Theme.Colours.border,
        width: '110px',
    },
    conditionCell: {
        ...Theme.Fonts.body,
        padding: '16px 24px',
        borderBottom: Theme.Colours.border,
        verticalAlign: 'top' as const,
    },
    channelCell: {
        ...Theme.Fonts.body,
        padding: '16px 24px',
        borderBottom: Theme.Colours.border,
        verticalAlign: 'top' as const,
    },
    textCell: {
        ...Theme.Fonts.body,
        padding: '16px 24px',
        borderBottom: Theme.Colours.border,
        verticalAlign: 'top' as const,
        whiteSpace: 'normal' as const,
        wordBreak: 'break-word' as const,
    },
    actionsCell: {
        ...Theme.Fonts.body,
        padding: '12px 24px',
        borderBottom: Theme.Colours.border,
        verticalAlign: 'top' as const,
        textAlign: 'right' as const,
    },
    rowButton: {
        marginLeft: 'auto',
    },
    emptyState: {
        padding: '24px',
        backgroundColor: Theme.Colours.workArea,
        border: Theme.Colours.border,
        borderRadius: '8px',
    },
    dialogOverlay: {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
    },
    dialog: {
        backgroundColor: Theme.Colours.workArea,
        borderRadius: '12px',
        padding: '32px',
        minWidth: '500px',
        maxWidth: '640px',
        maxHeight: 'calc(100vh - 48px)',
        overflowY: 'auto' as const,
        border: Theme.Colours.border,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    },
    dialogTitle: {
        ...Theme.Fonts.h2,
        margin: '0 0 24px 0',
        color: Theme.Colours.text,
    },
    formField: {
        marginBottom: '16px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
    },
    label: {
        ...Theme.Fonts.body,
        fontWeight: 'bold' as const,
        color: Theme.Colours.text,
    },
    helperText: {
        ...Theme.Fonts.body,
        color: Theme.Colours.text,
        margin: '0 0 24px 0',
    },
    input: {
        ...Theme.Fonts.body,
        width: '100%',
        padding: '12px 16px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        boxSizing: 'border-box' as const,
    },
    select: {
        ...Theme.Fonts.body,
        width: '100%',
        padding: '12px 16px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        boxSizing: 'border-box' as const,
    },
    textarea: {
        ...Theme.Fonts.body,
        width: '100%',
        minHeight: '120px',
        padding: '12px 16px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        boxSizing: 'border-box' as const,
        resize: 'vertical' as const,
    },
    fileInput: {
        ...Theme.Fonts.body,
        marginLeft: '0px',
        color: Theme.Colours.text,
    },
    sentenceBuilder: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '12px',
        alignItems: 'stretch',
    },
    sentenceRow: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: '16px',
        alignItems: 'center',
    },
    numberInput: {
        ...Theme.Fonts.body,
        width: '96px',
        marginLeft: '0px',
        padding: '12px 16px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        boxSizing: 'border-box' as const,
    },
    inlineSelect: {
        ...Theme.Fonts.body,
        marginLeft: '0px',
        padding: '12px 16px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        boxSizing: 'border-box' as const,
        minWidth: '160px',
    },
    channelOptions: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap' as const,
    },
    stageOptions: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        width: '100%',
    },
    stageOptionsBox: {
        minWidth: '320px',
        width: '100%',
        padding: '12px 16px',
        borderRadius: '12px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        boxSizing: 'border-box' as const,
    },
    stageOptionLabel: {
        ...Theme.Fonts.body,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: Theme.Colours.text,
    },
    channelOptionLabel: {
        ...Theme.Fonts.body,
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        color: Theme.Colours.text,
        whiteSpace: 'nowrap' as const,
    },
    inlineWideSelect: {
        ...Theme.Fonts.body,
        marginLeft: '0px',
        padding: '12px 16px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        boxSizing: 'border-box' as const,
        minWidth: '320px',
    },
    inlineText: {
        ...Theme.Fonts.body,
        color: Theme.Colours.text,
    },
    attachmentField: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        width: '100%',
    },
    attachmentHelperText: {
        ...Theme.Fonts.body,
        color: Theme.Colours.text,
        opacity: 0.8,
    },
    removeAttachmentButton: {
        ...Theme.Fonts.body,
        marginLeft: '0px',
        alignSelf: 'flex-start' as const,
        padding: '8px 12px',
        borderRadius: '6px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.workArea,
        color: Theme.Colours.text,
        cursor: 'pointer',
    },
    dialogButtons: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '32px',
    },
};
